import React from 'react';
import { BarChart3, TrendingUp, Save, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const AnalyticsTab = ({ analyticsSettings, setAnalyticsSettings, saveAnalyticsSettings }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg text-white mb-2">Analytics & Tracking</h3>
        <p className="text-white/50 text-sm">Configure website analytics to track visitor engagement and contact form conversions.</p>
      </div>

      {/* Google Analytics 4 */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Google Analytics 4</h4>
              <p className="text-white/40 text-sm">Industry standard, comprehensive analytics</p>
            </div>
          </div>
          <button
            onClick={() => setAnalyticsSettings({...analyticsSettings, ga4_enabled: !analyticsSettings.ga4_enabled})}
            data-testid="ga4-toggle"
            className={`relative w-12 h-6 rounded-full transition-colors ${
              analyticsSettings.ga4_enabled ? 'bg-green-500' : 'bg-white/20'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              analyticsSettings.ga4_enabled ? 'left-7' : 'left-1'
            }`} />
          </button>
        </div>
        <div className={analyticsSettings.ga4_enabled ? '' : 'opacity-50 pointer-events-none'}>
          <label className="text-white/50 text-sm block mb-2">Measurement ID</label>
          <Input 
            value={analyticsSettings.ga4_measurement_id}
            onChange={(e) => setAnalyticsSettings({...analyticsSettings, ga4_measurement_id: e.target.value})}
            className="bg-white/5 border-white/10 text-white"
            placeholder="G-XXXXXXXXXX"
          />
          <p className="text-white/30 text-xs mt-2">Find this in Google Analytics &rarr; Admin &rarr; Data Streams &rarr; Web</p>
        </div>
      </div>

      {/* Plausible Analytics */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Plausible Analytics</h4>
              <p className="text-white/40 text-sm">Privacy-focused, no cookie banner needed</p>
            </div>
          </div>
          <button
            onClick={() => setAnalyticsSettings({...analyticsSettings, plausible_enabled: !analyticsSettings.plausible_enabled})}
            data-testid="plausible-toggle"
            className={`relative w-12 h-6 rounded-full transition-colors ${
              analyticsSettings.plausible_enabled ? 'bg-green-500' : 'bg-white/20'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              analyticsSettings.plausible_enabled ? 'left-7' : 'left-1'
            }`} />
          </button>
        </div>
        <div className={analyticsSettings.plausible_enabled ? '' : 'opacity-50 pointer-events-none'}>
          <label className="text-white/50 text-sm block mb-2">Domain</label>
          <Input 
            value={analyticsSettings.plausible_domain}
            onChange={(e) => setAnalyticsSettings({...analyticsSettings, plausible_domain: e.target.value})}
            className="bg-white/5 border-white/10 text-white"
            placeholder="black-starpi.com"
          />
          <p className="text-white/30 text-xs mt-2">Your domain as registered in Plausible dashboard</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#D4AF37] text-sm font-medium">Conversion Tracking</p>
            <p className="text-white/50 text-sm mt-1">
              Contact form submissions are automatically tracked as conversion events in both analytics platforms when enabled.
            </p>
          </div>
        </div>
      </div>

      <Button onClick={saveAnalyticsSettings} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="save-analytics-btn">
        <Save className="w-4 h-4 mr-2" /> Save Analytics Settings
      </Button>
    </div>
  );
};
